import React, { useState, useEffect, useCallback } from 'react';
import { Search, Save, AlertTriangle, CheckCircle, XCircle, Layers, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ── helpers ─────────────────────────────────────────────────────── */
const stockStatus = (qty) => {
  if (qty === 0) return 'out';
  if (qty <= 5)  return 'low';
  return 'in';
};

const StatusBadge = ({ qty, label }) => {
  const s = stockStatus(qty);
  if (s === 'out') return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200" data-testid={`status-out-${label}`}>
      <XCircle size={11} /> Out of Stock
    </span>
  );
  if (s === 'low') return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200" data-testid={`status-low-${label}`}>
      <AlertTriangle size={11} /> Low ({qty})
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200" data-testid={`status-in-${label}`}>
      <CheckCircle size={11} /> In Stock
    </span>
  );
};

/* ── product row ─────────────────────────────────────────────────── */
const ProductRow = ({ product, localStock, onStockChange, onSave, saving }) => {
  const [expanded, setExpanded] = useState(false);
  const isSizewise = product.pricingType === 'sizewise';
  const ls = localStock[product._id];

  // Determine overall product status
  const productStatus = isSizewise
    ? (() => {
        if (!product.sizes?.length) return 'out';
        const allOut = product.sizes.every(s => (ls?.sizes?.[s.label] ?? s.stock) === 0);
        const anyLow = product.sizes.some(s => {
          const q = ls?.sizes?.[s.label] ?? s.stock;
          return q > 0 && q <= 5;
        });
        const anyOut = product.sizes.some(s => (ls?.sizes?.[s.label] ?? s.stock) === 0);
        if (allOut) return 'out';
        if (anyOut || anyLow) return 'low';
        return 'in';
      })()
    : stockStatus(ls?.stock ?? product.stock);

  const statusColor = { out: 'border-l-red-400', low: 'border-l-amber-400', in: 'border-l-green-400' }[productStatus];

  return (
    <div className={`bg-white rounded-xl border border-[#ECE0DA] border-l-4 ${statusColor} shadow-sm overflow-hidden`} data-testid={`stock-row-${product._id}`}>
      {/* Product header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Thumbnail */}
        <img
          src={product.images?.[0] || 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=60'}
          alt={product.name}
          className="w-10 h-10 rounded-lg object-cover border border-[#ECE0DA] shrink-0"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?w=60'; }}
        />

        {/* Name + category */}
        <div className="flex-1 min-w-0">
          <p className="font-playfair font-semibold text-[#1F1410] text-sm truncate">{product.name}</p>
          <p className="text-[10px] text-[#7A6A63]">{product.category?.name || '—'} · {isSizewise ? `${product.sizes?.length} sizes` : 'Single SKU'}</p>
        </div>

        {/* Status badge */}
        <div className="hidden sm:block shrink-0">
          {isSizewise ? (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
              productStatus === 'out' ? 'bg-red-50 text-red-600 border border-red-200' :
              productStatus === 'low' ? 'bg-amber-50 text-amber-600 border border-amber-200' :
              'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {productStatus === 'out' ? 'All Out' : productStatus === 'low' ? 'Partially Low' : 'All In Stock'}
            </span>
          ) : (
            <StatusBadge qty={ls?.stock ?? product.stock} label={product._id} />
          )}
        </div>

        {/* Single SKU stock input */}
        {!isSizewise && (
          <div className="flex items-center gap-2 shrink-0">
            <input
              type="number"
              min="0"
              value={ls?.stock ?? product.stock}
              onChange={e => onStockChange(product._id, null, Number(e.target.value))}
              className="w-20 px-2 py-1.5 border border-[#ECE0DA] rounded-lg text-sm text-center focus:outline-none focus:border-[#7A1125] focus:ring-1 focus:ring-[#7A1125]/20"
              data-testid={`stock-input-${product._id}`}
            />
            <button
              onClick={() => onSave(product._id)}
              disabled={saving[product._id]}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
              data-testid={`save-stock-${product._id}`}
            >
              {saving[product._id] ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
              {saving[product._id] ? '' : 'Save'}
            </button>
          </div>
        )}

        {/* Sizewise: expand toggle + save */}
        {isSizewise && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => onSave(product._id)}
              disabled={saving[product._id]}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#7A1125] hover:bg-[#8B1A2B] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
              data-testid={`save-stock-${product._id}`}
            >
              {saving[product._id] ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />}
              {saving[product._id] ? '' : 'Save'}
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 rounded-lg border border-[#ECE0DA] hover:bg-[#FDF8F3] transition-colors"
              data-testid={`expand-sizes-${product._id}`}
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          </div>
        )}
      </div>

      {/* Size variant rows */}
      {isSizewise && expanded && (
        <div className="border-t border-[#ECE0DA] bg-[#FDF8F3]">
          {product.sizes?.map((size, idx) => {
            const qty = ls?.sizes?.[size.label] ?? size.stock;
            return (
              <div
                key={size.label}
                className={`flex items-center gap-3 px-4 py-2.5 ${idx < product.sizes.length - 1 ? 'border-b border-[#ECE0DA]' : ''}`}
                data-testid={`size-row-${product._id}-${size.label}`}
              >
                <div className="w-10 shrink-0" /> {/* spacer aligns with thumbnail */}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-[#1F1410]">{size.label}</span>
                  <span className="text-xs text-[#7A6A63] ml-2">₹{size.price}</span>
                </div>
                <StatusBadge qty={qty} label={`${product._id}-${size.label}`} />
                <input
                  type="number"
                  min="0"
                  value={qty}
                  onChange={e => onStockChange(product._id, size.label, Number(e.target.value))}
                  className={`w-20 px-2 py-1.5 border rounded-lg text-sm text-center focus:outline-none focus:ring-1 transition-colors ${
                    qty === 0 ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-200' :
                    qty <= 5 ? 'border-amber-300 bg-amber-50 focus:border-amber-400 focus:ring-amber-200' :
                    'border-[#ECE0DA] focus:border-[#7A1125] focus:ring-[#7A1125]/20'
                  }`}
                  data-testid={`size-input-${product._id}-${size.label}`}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── main page ───────────────────────────────────────────────────── */
const StockManagement = () => {
  const { authHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [localStock, setLocalStock] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/products`, { headers: authHeader }),
      axios.get(`${API}/categories`),
    ]).then(([pRes, cRes]) => {
      setProducts(pRes.data);
      setCategories(cRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  // Initialise local stock mirrors when products load
  useEffect(() => {
    const init = {};
    products.forEach(p => {
      if (p.pricingType === 'single') {
        init[p._id] = { type: 'single', stock: p.stock };
      } else {
        const sizes = {};
        (p.sizes || []).forEach(s => { sizes[s.label] = s.stock; });
        init[p._id] = { type: 'sizewise', sizes };
      }
    });
    setLocalStock(init);
  }, [products]);

  const handleStockChange = useCallback((productId, sizeLabel, value) => {
    setLocalStock(prev => {
      const entry = prev[productId] || {};
      if (!sizeLabel) return { ...prev, [productId]: { ...entry, stock: Math.max(0, value) } };
      return { ...prev, [productId]: { ...entry, sizes: { ...entry.sizes, [sizeLabel]: Math.max(0, value) } } };
    });
  }, []);

  const handleSave = useCallback(async (productId) => {
    setSaving(s => ({ ...s, [productId]: true }));
    try {
      const ls = localStock[productId];
      const payload = ls.type === 'single'
        ? { stock: ls.stock }
        : { sizes: Object.entries(ls.sizes).map(([label, stock]) => ({ label, stock })) };
      const { data } = await axios.put(`${API}/products/${productId}/stock`, payload, { headers: authHeader });
      setProducts(prev => prev.map(p => p._id === productId ? data : p));
      toast.success('Stock updated!');
    } catch {
      toast.error('Failed to update stock');
    } finally {
      setSaving(s => ({ ...s, [productId]: false }));
    }
  }, [localStock, authHeader]);

  /* derive product-level status for filtering */
  const getStatus = useCallback((p) => {
    const ls = localStock[p._id];
    if (p.pricingType === 'single') return stockStatus(ls?.stock ?? p.stock);
    const sizes = p.sizes || [];
    if (!sizes.length) return 'out';
    if (sizes.every(s => (ls?.sizes?.[s.label] ?? s.stock) === 0)) return 'out';
    if (sizes.some(s => { const q = ls?.sizes?.[s.label] ?? s.stock; return q === 0 || q <= 5; })) return 'low';
    return 'in';
  }, [localStock]);

  const filtered = products.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (catFilter && p.category?._id !== catFilter) return false;
    if (statusFilter !== 'all') {
      const s = getStatus(p);
      if (statusFilter === 'out' && s !== 'out') return false;
      if (statusFilter === 'low' && s !== 'low') return false;
      if (statusFilter === 'in' && s !== 'in') return false;
    }
    return true;
  });

  const totalOut = products.filter(p => getStatus(p) === 'out').length;
  const totalLow = products.filter(p => getStatus(p) === 'low').length;
  const totalIn  = products.filter(p => getStatus(p) === 'in').length;

  return (
    <div className="p-4 sm:p-6 bg-[#FDF8F3] min-h-full" data-testid="admin-stock-management">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-0.5">
          <Layers size={20} className="text-[#7A1125]" />
          <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Stock Management</h1>
        </div>
        <p className="text-[#7A6A63] text-sm">Update stock quantities per product and size. Status updates automatically.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="stock-summary-cards">
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
          <p className="text-xl font-bold font-playfair text-green-700">{totalIn}</p>
          <p className="text-xs text-green-600 font-medium">In Stock</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
          <p className="text-xl font-bold font-playfair text-amber-700">{totalLow}</p>
          <p className="text-xs text-amber-600 font-medium">Low Stock</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
          <p className="text-xl font-bold font-playfair text-red-700">{totalOut}</p>
          <p className="text-xs text-red-600 font-medium">Out of Stock</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[160px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6A63]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-8 pr-3 py-2 border border-[#ECE0DA] rounded-lg text-sm focus:outline-none focus:border-[#7A1125]"
            data-testid="stock-search"
          />
        </div>
        {/* Category */}
        <select
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
          className="px-3 py-2 border border-[#ECE0DA] rounded-lg text-sm focus:outline-none focus:border-[#7A1125] text-[#7A6A63] bg-white"
          data-testid="stock-category-filter"
        >
          <option value="">All Categories</option>
          {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        {/* Status filter */}
        <div className="flex border border-[#ECE0DA] rounded-lg overflow-hidden bg-white">
          {[['all','All'],['in','In Stock'],['low','Low'],['out','Out']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setStatusFilter(val)}
              className={`px-3 py-2 text-xs font-medium transition-colors ${statusFilter === val ? 'bg-[#7A1125] text-white' : 'text-[#7A6A63] hover:bg-[#FDF8F3]'}`}
              data-testid={`stock-filter-${val}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Product list */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#7A6A63]">
          <Layers size={40} className="mx-auto mb-3 text-[#ECE0DA]" />
          <p className="font-playfair text-lg">No products found</p>
          <p className="text-sm mt-1">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="stock-product-list">
          {filtered.map(product => (
            <ProductRow
              key={product._id}
              product={product}
              localStock={localStock}
              onStockChange={handleStockChange}
              onSave={handleSave}
              saving={saving}
            />
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="mt-6 flex flex-wrap gap-4 text-xs text-[#7A6A63] border-t border-[#ECE0DA] pt-4">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block" /> In Stock: qty &gt; 5</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" /> Low Stock: qty 1–5</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-400 inline-block" /> Out of Stock: qty = 0</span>
        <span className="ml-auto italic">Single-SKU products are auto-deactivated when stock = 0</span>
      </div>
    </div>
  );
};

export default StockManagement;
